import React, { useState } from 'react';
import { Download, FileText, FileBarChart, Loader2, ChevronDown } from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';

const ExportDocumentsDropdown = ({ 
  projectId, 
  tenantId, 
  baselineId = null,
  buttonText = "Export Documents",
  className = "",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(null); // 'scope-pack' or 'pmo-summary'

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleExportScopePack = async () => {
    if (!projectId || !tenantId) {
      toast.error('Project not selected');
      return;
    }

    setLoading('scope-pack');
    try {
      const response = await api.generateScopePack({
        tenant_id: tenantId,
        project_id: projectId,
        baseline_id: baselineId,
        include_ac: true,
        include_risks: true,
        include_constraints: true,
        include_dependencies: true,
        include_wbs: true,
      });

      const filename = response.headers['content-disposition']
        ?.split('filename=')[1]
        ?.replace(/"/g, '') || 'ScopePack.pdf';

      downloadBlob(response.data, filename);
      toast.success('Scope Pack downloaded');
    } catch (err) {
      console.error('Failed to generate Scope Pack:', err);
      toast.error('Failed to generate Scope Pack');
    } finally {
      setLoading(null);
      setIsOpen(false);
    }
  };

  const handleExportPMOSummary = async () => {
    if (!projectId || !tenantId) {
      toast.error('Project not selected');
      return;
    }

    setLoading('pmo-summary');
    try {
      const response = await api.generatePMOSummary({
        tenant_id: tenantId,
        project_id: projectId,
        baseline_id: baselineId,
      });

      const filename = response.headers['content-disposition']
        ?.split('filename=')[1]
        ?.replace(/"/g, '') || 'PMOSummary.pdf';

      downloadBlob(response.data, filename);
      toast.success('PMO Summary downloaded');
    } catch (err) {
      console.error('Failed to generate PMO Summary:', err);
      toast.error('Failed to generate PMO Summary');
    } finally {
      setLoading(null);
      setIsOpen(false);
    }
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={loading || disabled}
        className={`flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-secondary)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${disabled ? 'pointer-events-none' : ''}`}
        data-testid="export-documents-btn"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Download className="w-4 h-4" />
        )}
        <span>{buttonText}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && !loading && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-20 overflow-hidden">
            <div className="py-1">
              <button
                onClick={handleExportScopePack}
                disabled={loading === 'scope-pack'}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                data-testid="export-scope-pack-btn"
              >
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileText className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">Scope Pack</p>
                  <p className="text-xs text-gray-500">Full requirements & WBS</p>
                </div>
              </button>

              <button
                onClick={handleExportPMOSummary}
                disabled={loading === 'pmo-summary'}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                data-testid="export-pmo-summary-btn"
              >
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileBarChart className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">PMO Summary</p>
                  <p className="text-xs text-gray-500">Executive overview</p>
                </div>
              </button>
            </div>

            <div className="px-4 py-2 bg-gray-50 border-t">
              <p className="text-xs text-gray-500">
                PDFs include tenant branding
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ExportDocumentsDropdown;
